import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Checkbox } from 'components/UIElements'
import STRINGS from 'utils/strings'
import {
  TIMED_MATCHING_GRID_INPUT_TYPE,
  TIMED_MATCHING_GRID_INPUT_TYPE_OPTIONS,
  TIMED_MATCHING_GRID_KEY_SLOT_COUNT,
} from 'utils/constants'
import {
  getGridCellCount,
  getSymbolKeyEntry,
  getTimedMatchingGridDetails,
  getTimedMatchingGridPreviewSequence,
} from 'utils/timedMatchingGrid'
import TimedMatchingGridSymbol from './TimedMatchingGridSymbol'
import './TimedMatchingGrid.scss'

const TimedMatchingGridPreview = ({ item }) => {
  const details = getTimedMatchingGridDetails(item)
  const rows = Number(details.grid_rows) || 0
  const columns = Number(details.grid_columns) || 0
  const total = getGridCellCount(rows, columns)
  const symbolKey = details.symbol_key || []
  const isCheckmark = details.input_type === TIMED_MATCHING_GRID_INPUT_TYPE.checkmark
  const sequence = useMemo(
    () => getTimedMatchingGridPreviewSequence(details),
    [details.randomize_items, details.grid_rows, details.grid_columns, details.item_sequence],
  )

  return (
    <div className='tmg-participant-preview' aria-readonly='true'>
      <div className='tmg-legend' role='table' aria-label={STRINGS.tmgSymbolKey}>
        <div className='tmg-legend-row' role='row'>
          {Array.from({ length: TIMED_MATCHING_GRID_KEY_SLOT_COUNT }, (_, i) => {
            const digit = i + 1
            const entry = getSymbolKeyEntry(symbolKey, digit)
            return (
              <div key={`legend-symbol-${digit}`} className='tmg-legend-cell tmg-legend-symbol' role='cell'>
                <TimedMatchingGridSymbol entry={entry} alt={`Symbol ${digit}`} />
              </div>
            )
          })}
        </div>
        <div className='tmg-legend-row' role='row'>
          {Array.from({ length: TIMED_MATCHING_GRID_KEY_SLOT_COUNT }, (_, i) => (
            <div key={`legend-digit-${i + 1}`} className='tmg-legend-cell tmg-legend-digit' role='cell'>
              {i + 1}
            </div>
          ))}
        </div>
      </div>

      <p className='tmg-preview-guidance'>
        {isCheckmark ? STRINGS.tmgPreviewCheckmarkGuidance : STRINGS.tmgPreviewDigitGuidance}
      </p>

      <div
        className={`tmg-participant-grid${isCheckmark ? ' checkmark' : ' digit'}`}
        style={{ '--tmg-cols': columns || 1 }}
        role='img'
        aria-label={`${rows} by ${columns} matching grid`}>
        {Array.from({ length: rows }, (_, rowIndex) => {
          const start = rowIndex * columns
          const rowValues = sequence.slice(start, start + columns)
          return (
            <div key={`tmg-preview-row-${rowIndex}`} className='tmg-participant-row'>
              <div className='tmg-participant-subrow'>
                {rowValues.map((value, colIndex) => {
                  const entry = value ? getSymbolKeyEntry(symbolKey, value) : null
                  return (
                    <div
                      key={`tmg-preview-symbol-${rowIndex}-${colIndex}`}
                      className='tmg-participant-subcell tmg-participant-symbol'>
                      <TimedMatchingGridSymbol entry={entry} alt={value ? `Symbol ${value}` : ''} />
                    </div>
                  )
                })}
              </div>
              <div className='tmg-participant-subrow tmg-participant-answers'>
                {rowValues.map((_, colIndex) => (
                  <div
                    key={`tmg-preview-answer-${rowIndex}-${colIndex}`}
                    className={`tmg-participant-subcell${isCheckmark ? ' tmg-participant-check' : ' tmg-participant-input'}`}
                    aria-hidden='true'
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      <div className='tmg-preview-meta'>
        <div className='tmg-preview-meta-row'>
          <span className='tmg-preview-meta-label'>{STRINGS.tmgTimeLimit}</span>
          <span className='tmg-preview-meta-value'>{details.time_limit_seconds}</span>
        </div>
        <div className='tmg-preview-meta-row'>
          <span className='tmg-preview-meta-label'>{STRINGS.tmgPreviewTotalItems}</span>
          <span className='tmg-preview-meta-value'>
            {STRINGS.tmgPreviewTotalItemsDetail
              .replace('{rows}', rows)
              .replace('{columns}', columns)
              .replace('{total}', total)}
          </span>
        </div>
        <div className='tmg-preview-meta-row tmg-preview-meta-toggle'>
          <span className='tmg-preview-meta-label'>{STRINGS.tmgRandomization}</span>
          <Checkbox toggle disabled checked={!!details.randomize_items} onClick={() => {}} />
        </div>
        <div className='tmg-preview-meta-row'>
          <span className='tmg-preview-meta-label'>{STRINGS.tmgInputType}</span>
          <span className='tmg-preview-meta-value'>
            {TIMED_MATCHING_GRID_INPUT_TYPE_OPTIONS.find(option => option.value === details.input_type)?.text ||
              details.input_type}
          </span>
        </div>
      </div>
    </div>
  )
}

TimedMatchingGridPreview.propTypes = {
  item: PropTypes.shape({
    attributes: PropTypes.shape({
      timed_matching_grid_details: PropTypes.shape({}),
    }),
  }),
}

export default TimedMatchingGridPreview
